import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { ConfirmedDeal, Visita } from '../../types/empresa';
import { Link, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../api/auth/authSlice';
import { useChangeDealStageMutation } from '../../api/pipelineApi';
import { ChangeStage, ChangeStageComment } from '../../types/pipeline';
import { Dispatch, SetStateAction, useState } from 'react';
import { CircularProgress } from '@mui/joy';
import { ModalContainer } from '../UtilitiesComponents/ModalContainer';
import { ConfirmarCancelacion } from './Calendario/ConfirmarCancelacion';
import { EditarDealForm } from './EditarDealForm';
import { VendedorItem } from './Calendario/VendedorItem';

interface Props {
    deal: ConfirmedDeal;
    setCloseModal: Dispatch<SetStateAction<boolean>>;
}

export function ConfirmedDealCard({ deal, setCloseModal }: Props) {
    const location = useLocation();
    const user = useSelector(selectCurrentUser);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);

    const [changeDealStage, { isLoading }] = useChangeDealStageMutation();

    const handleCancelDeal = async (eliminarVendedores: boolean) => {
        try {
            const newStage: ChangeStage = {
                dealId: deal.dealId,
                newStageId: 3,
                pipelineId: 1,
                empresaId: deal.empresaId,
                stageName: "old stage",
                cancelacion: true,
                eliminarVendedores,
            };

            const comment: ChangeStageComment = {
                motivo: "Cancelacion de la campaña",
                comentario: "Cancelacion de la campaña desde el calendario",
                visitaInfo: {} as Visita,
                horaInicio: '',
                horaFin: '',
            }
            await changeDealStage({...newStage, ...comment}).unwrap();

            setShowModal(false);
            setCloseModal(false);

        } catch (error) {
            console.error(error);
        }
    }

    return (
        <div 
            className="p-[12px] flex gap-x-5 rounded-xl shadow-sm h-[120px] border border-gray-300 my-4">

            <div className="bg-green-200 rounded-md flex flex-col justify-center items-center p-3">
                <p className="font-semibold">{deal.fechaVisita}</p>
                <p className="font-semibold">{deal.horarioVisita}</p>
            </div>

            <div
                className="h-full border-l border-gray-300">
            </div>

            <div className="w-[300px]">
                <div className="flex gap-x-1 items-center">
                    <BusinessRoundedIcon sx={{ width: 25, height: 25 }} />
                    {user != null && user.id === deal.prospectadorId
                    ?
                    <Link 
                        className="w-fit" 
                        to={`/empresa/${deal.empresaId}`}
                        state={{ fromName: "calendario (empresas)", fromPath: location.pathname }}>
                        <p className="font-semibold text-xl">{deal.nombre}</p>
                    </Link>
                    : <p className="font-semibold text-xl">{deal.nombre}</p>
                    }
                </div>

                <div>
                    <p className="font-semibold text-sm">Sucursal 1</p>
                </div>

                <div className="grid grid-cols-2 gap-x-5">
                    <p className="font-semibold">Tipo de empresa</p>
                    <p>{deal.tipoEmpresa}</p>
                </div>

                <div className="grid grid-cols-2 gap-x-5">
                    <p className="font-semibold">Horario de la visita</p>
                    <p>{deal.horarioVisita}</p>
                </div>

            </div>

            <div
                className="h-full border-l border-gray-300">
            </div>

            <div className="h-full w-[140px]">
                <p className="text-[13px] font-semibold">Prospectador: </p>
                <div className="flex gap-x-1 items-center">
                    <div className="bg-gray-300 rounded-full w-[20px] h-[20px]"></div>
                    <p>{deal.prospectadorName}</p>
                </div>
            </div>
                
            <div
                className="h-full border-l border-gray-300">
            </div>

            <div className="h-full w-[320px]">
                <p className="text-[13px] font-semibold">Vendedores: </p>
                <ul className="overflow-auto max-h-[75px]">
                    {deal.vendedores.length == 0
                    ? <p className="italic">No hay vendedores asignados a esta campaña</p>
                    :
                    <>
                    {deal.vendedores.map((vendedor, index) => (
                        <VendedorItem 
                            prospectadorId={deal.prospectadorId}
                            userId={user?.id ?? 0}
                            key={index}
                            dealId={deal.dealId}
                            empresaId={deal.empresaId}
                            vendedorId={vendedor.id}
                            nombreVendedor={vendedor.nombre}/>
                        ))}
                    </>}
                </ul>
            </div>

            <div
                className="h-full border-l border-gray-300">
            </div>

            {user != null && deal.prospectadorId === user.id && (
            <div className="h-full">
                <div className="h-full flex items-center justify-center flex-col gap-y-2">
                    {isLoading
                    ? <CircularProgress />
                    : 
                    <button
                        onClick={() => setShowModal(true)}
                        className="bg-red-400 border-2 border-red-500 px-1 py-[2px] rounded-[3px] hover:bg-red-500">
                        <p className="text-sm font-semibold text-white">Cancelar campaña</p>
                    </button>
                    }

                    <button 
                        onClick={() => setShowEditModal(true)}
                        className="bg-blue-400 border-2 border-blue-500 px-1 py-[2px] rounded-[3px] hover:bg-blue-500">
                        <p className="text-sm font-semibold text-white">Editar campaña</p>
                    </button>
                </div>
            </div>
            )}

            {showModal && (
                <ModalContainer>
                    <ConfirmarCancelacion 
                        upadateDeal={handleCancelDeal}
                        dealName={deal.nombre}
                        setShowModal={setShowModal}/>
                </ModalContainer>
            )}

            {showEditModal && (
                <ModalContainer>
                    <EditarDealForm
                        deal={deal}
                        setShowModal={setShowEditModal}/>
                </ModalContainer>
            )}
        </div>
    );
}